import { MouseEventHandler, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Box } from '@mui/material';
import { GridRowModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
    openDrawer,
    setCurrentDrawerId,
    setCurrentEntity,
    setCurrentOperation,
    setCurrentTenantId
} from 'common/components/EditDrawer/editDrawerSlice';
import { UserProfileEntityContainerWithAccordeon } from 'common/components/UserProfileEntityContainer/UserProfileEntityContainerWithAccordeon';
import { UserProfilesDataGrid } from 'common/components/UserProfilesDataGrid/UserProfilesDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchUserProfileRoles } from 'common/services/api/UserProfileRoles/UserProfileRoles.thunk';
import {
    ActionDefinition,
    ColumnDefinition,
    ColumnType,
    DrawerEntitiesNames,
    Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';
import { UserRoleMembershipCreateContent } from 'features/userRoleMemberships/UserRoleMembershipCreateDrawerContent';
import { useSnackbar } from 'notistack';

import { UserProfileRolesDeleteDialog } from './UserProfileRolesDelete';
import {
    selectProcessing,
    selectUserProfileRolesByTenantId,
} from './userProfileRolesSlice';

const CONTAINER_INLINE_STYLES = {
    boxShadow: 'unset',
    marginBottom: '1rem',
    position: 'unset',
    backgroundColor: 'secondary.light',
    borderRadius: '4px',
    border: 0.5,
    borderColor: 'info.dark',
    flexBasis: '100%',
    marginTop: '24px',
    height: 'max-content'
};

type UserProfileRolesListProps = {
    tenant: IUserProfileTenant;
}

export function UserProfileRolesList({ tenant }: Readonly<UserProfileRolesListProps>) {

    const params = useParams();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const userProfileRoles = useAppSelector(selectUserProfileRolesByTenantId(tenant.id));
    const processing = useAppSelector(selectProcessing);

    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager, Roles.AppManager, Roles.MultiTenantRO ]);
    const shouldDisableLinkButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);

    const [sortModel, setSortModel] = usePalmSortModel();

    useEffect(() => {
        if (userProfileRoles) {
            return;
        }
        const fetchUserProfileRolesParams = {
            tenantId: tenant.id,
            userId: params?.userId ?? ''
        };
        dispatch(fetchUserProfileRoles(fetchUserProfileRolesParams))
            .catch((error) => {
                handleListFetchError(extractStatusCode(error.message), 'User Profile Roles', enqueueSnackbar);
            });
    }, []);

    const handleCreateButtonClick: MouseEventHandler<HTMLButtonElement> = event => {
        dispatch(setCurrentOperation(Operations.Link));
        dispatch(setCurrentEntity(DrawerEntitiesNames.UserProfileRolesMembership));
        dispatch(setCurrentTenantId(tenant.id));
        dispatch(setCurrentDrawerId(DrawerIDs.UserRoleMembership));
        dispatch(openDrawer());
        event.stopPropagation();
    };

    const disabledDeleteCallback = (row: GridRowModel) => {
        if (shouldDisableLinkButton) {
            return true;
        }

        if (row.userGroupName) {
            return true;
        }

        return false;
    };

    const getRowLink = useCallback((roleId: string, row: GridRowModel | undefined) => {
        if(row?.userGroupName) {
            return `/${Routes.ApplicationSuites}/${tenant.applicationSuiteId}/${Routes.Tenants}/${tenant.id}/${Routes.Roles}/${roleId}/${Routes.UserGroups}`;
        } else {
            return `/${Routes.ApplicationSuites}/${tenant.applicationSuiteId}/${Routes.Tenants}/${tenant.id}/${Routes.Roles}/${roleId}/${Routes.Users}`;
        }
    }, []);

    const handleOpenButtonClick = useCallback((roleId: string, row: GridRowModel | undefined) => {
        const link = getRowLink(roleId, row);
        navigate(link);
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: getRowLink },
        { action: Operations.Delete, disabled: disabledDeleteCallback }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'name', columnName: 'Role Name', columnType: ColumnType.First, copy: false },
        { columnId: 'userGroupName', columnName: 'Inheritance', columnType: ColumnType.Tooltip, copy: false, headerPrompt: 'Inherited items cannot be deleted' },
        { columnId: 'actions', columnName: 'Action', size: 0.6, columnType: ColumnType.Action, sortable: false }
    ];

    return (
        <UserProfileEntityContainerWithAccordeon
            title='Roles'
            isCreateButtonDisabled={shouldDisableLinkButton}
            shouldShowCreateButton={true}
            containerInlineStyles={CONTAINER_INLINE_STYLES}
            createButtonHandler={handleCreateButtonClick}
        >
            <Box sx={{ margin: '0 24px 24px 24px' }}>
                <UserProfilesDataGrid
                    tenant={tenant}
                    rows={userProfileRoles}
                    actionDefinitions={actionDefinitions}
                    columnDefinitions={columnDefinitions}
                    processing={processing === tenant.id}
                    currentEntityType={DrawerEntitiesNames.UserProfileRolesMembership}
                    title='Roles'
                    contentFunction={UserRoleMembershipCreateContent}
                    deleteFunction={UserProfileRolesDeleteDialog}
                    customDeleteTitle={'Are you sure you want to remove this Role from this User?'}
                    customCreateEditTitle='Role'
                    rowClick={shouldDisableOpenButton ? undefined : handleOpenButtonClick}
                    rowLink={getRowLink}
                    sortModel={sortModel}
                    clientSortHandler={setSortModel}
                />
            </Box>
        </UserProfileEntityContainerWithAccordeon>
    );
}
